import Ajv2020, { type ValidateFunction } from "ajv/dist/2020";

const AI_CATALOG_MESSAGE_TYPE = "aavo:custom-app:ai-catalog";
const AI_CATALOG_ERROR_MESSAGE_TYPE = "aavo:custom-app:ai-catalog-error";
const AI_TOOL_CALL_MESSAGE_TYPE = "aavo:custom-app:ai-tool-call";
const AI_TOOL_RESULT_MESSAGE_TYPE = "aavo:custom-app:ai-tool-result";
const AI_TOOL_ERROR_MESSAGE_TYPE = "aavo:custom-app:ai-tool-error";
const AI_MESSAGE_MESSAGE_TYPE = "aavo:custom-app:ai-message";
const AI_MESSAGE_RESULT_MESSAGE_TYPE = "aavo:custom-app:ai-message-result";
const AI_MESSAGE_ERROR_MESSAGE_TYPE = "aavo:custom-app:ai-message-error";
export const MAX_AI_MESSAGE_LENGTH = 10_000;
export const MAX_AI_MESSAGE_LABEL_LENGTH = 200;
const AI_SKILL_NAME = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const MAX_AI_SKILL_NAME_LENGTH = 64;
const MAX_AI_GUIDANCE_LENGTH = 32_000;
const MAX_AI_TOOL_DESCRIPTION_LENGTH = 2000;
const MAX_AI_TOOLS = 50;
const MAX_AI_CONVERSATION_KEY_LENGTH = 200;
const MAX_AI_CONVERSATION_LABEL_LENGTH = 200;
const AI_TOOL_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
// The agent runtime waits 30 seconds, so the host reports a timeout first.
const DEFAULT_AI_TOOL_TIMEOUT_MS = 25_000;
/** JSON Schema format of string fields that hold a file. In the call messages a file is a token string. */
export const AI_FILE_FORMAT = "aavo-file";
const AI_FILE_TOKEN_PATTERN = /^aavo-file:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
export const MAX_AI_TOOL_FILES = 10;
export const MAX_AI_TOOL_FILE_BYTES = 25 * 1024 * 1024;
export const MAX_AI_TOOL_TOTAL_FILE_BYTES = 50 * 1024 * 1024;

export interface CustomAppAiTool {
	key: string;
	description: string;
	inputSchema: Record<string, unknown>;
	outputSchema?: Record<string, unknown>;
}

/** The keyed Aavo AI conversation an app has opened with `useKeyedAiConversation`. */
export interface CustomAppAiConversation {
	key: string;
	label: string | null;
}

/** AI guidance, tools, and keyed conversation that a Custom App has registered with the SDK. */
export interface CustomAppAiCatalog {
	guidance: string | null;
	tools: CustomAppAiTool[];
	conversation: CustomAppAiConversation | null;
}

export class CustomAppAiToolCallError extends Error {
	constructor(
		readonly code: string,
		message: string,
	) {
		super(message);
		this.name = "CustomAppAiToolCallError";
	}
}

/**
 * A message the app sends to Aavo AI from a user action. The conversation shows [label] in place of [message], and
 * Aavo AI uses the app's AI skill [skill] for it.
 */
export interface CustomAppAiMessage {
	message: string;
	label: string;
	skill?: string;
}

/** A message the app sent could not start an Aavo AI run. The app receives [code] and the message. */
export class CustomAppAiMessageError extends Error {
	constructor(
		readonly code: string,
		message: string,
	) {
		super(message);
		this.name = "CustomAppAiMessageError";
	}
}

export type CustomAppAiMessageHandler = (message: CustomAppAiMessage) => Promise<void>;

/** The output of an AI tool and the files it returned, keyed by the token that stands for each in the output. */
export interface CustomAppAiToolResult {
	output: unknown;
	files: ReadonlyMap<string, File>;
}

export interface CustomAppAiConnection {
	catalog(): CustomAppAiCatalog;
	subscribe(listener: () => void): () => void;
	/** True while an AI tool call waits for the app. */
	isCallInProgress(): boolean;
	/** Runs a tool. Each token of [files] in the input is replaced by its file before the app receives it. */
	callTool(toolKey: string, input: unknown, files?: ReadonlyMap<string, File>): Promise<CustomAppAiToolResult>;
	/** Sets who starts an Aavo AI run for a message the app sends. Without a handler, messages are rejected. */
	setMessageHandler(handler: CustomAppAiMessageHandler | null): void;
}

interface CatalogEntry {
	definition: CustomAppAiTool;
	validateInput: ValidateFunction;
	validateOutput: ValidateFunction | undefined;
}

interface PendingCall {
	entry: CatalogEntry;
	resolve: (result: CustomAppAiToolResult) => void;
	reject: (error: CustomAppAiToolCallError) => void;
	timer: ReturnType<typeof setTimeout>;
}

const EMPTY_CATALOG: CustomAppAiCatalog = { guidance: null, tools: [], conversation: null };

export function createCustomAppAiChannel(
	send: (message: unknown) => void,
	{
		timeoutMs = DEFAULT_AI_TOOL_TIMEOUT_MS,
		isUserActivationActive = () => navigator.userActivation?.isActive === true,
	}: { timeoutMs?: number; isUserActivationActive?: () => boolean } = {},
) {
	// Each catalog compiles with its own Ajv instance: a retained schema `$id` would reject the next catalog.
	let ajv = createAjv();
	let catalog = EMPTY_CATALOG;
	let entries = new Map<string, CatalogEntry>();
	const listeners = new Set<() => void>();
	const pending = new Map<string, PendingCall>();
	let messageHandler: CustomAppAiMessageHandler | null = null;
	let disposed = false;

	function replaceCatalog(next: CustomAppAiCatalog, nextEntries: Map<string, CatalogEntry>) {
		catalog = next;
		entries = nextEntries;
		for (const listener of listeners) listener();
	}

	function handleCatalogMessage(message: Record<string, unknown>) {
		try {
			const parsed = parseCatalog(message);
			const nextAjv = createAjv();
			const nextEntries = new Map<string, CatalogEntry>();
			for (const definition of parsed.tools) {
				nextEntries.set(definition.key, {
					definition,
					validateInput: compileSchema(nextAjv, definition.key, "input", definition.inputSchema),
					validateOutput:
						definition.outputSchema === undefined ?
							undefined
						:	compileSchema(nextAjv, definition.key, "output", definition.outputSchema),
				});
			}
			ajv = nextAjv;
			replaceCatalog(parsed, nextEntries);
		} catch (error) {
			replaceCatalog(EMPTY_CATALOG, new Map());
			send({
				type: AI_CATALOG_ERROR_MESSAGE_TYPE,
				message: error instanceof Error ? error.message : "The AI catalog is invalid.",
			});
		}
	}

	function finishCall(requestId: string, message: Record<string, unknown>) {
		const call = pending.get(requestId);
		if (!call) return;
		pending.delete(requestId);
		clearTimeout(call.timer);
		if (message.type === AI_TOOL_ERROR_MESSAGE_TYPE) {
			const error = isRecord(message.error) ? message.error : {};
			call.reject(
				new CustomAppAiToolCallError(
					typeof error.code === "string" ? error.code : "AI_TOOL_FAILED",
					typeof error.message === "string" && error.message !== "" ?
						error.message
					:	`AI tool '${call.entry.definition.key}' failed.`,
				),
			);
			return;
		}
		const output = message.output ?? null;
		if (call.entry.validateOutput && !call.entry.validateOutput(output)) {
			call.reject(
				new CustomAppAiToolCallError(
					"INVALID_OUTPUT",
					`AI tool '${call.entry.definition.key}' returned output that does not match its output schema: ` +
						ajv.errorsText(call.entry.validateOutput.errors),
				),
			);
			return;
		}
		try {
			call.resolve({ output, files: parseOutputFiles(call.entry.definition.key, output, message.files) });
		} catch (error) {
			call.reject(
				new CustomAppAiToolCallError(
					"INVALID_OUTPUT",
					error instanceof Error ?
						error.message
					:	`AI tool '${call.entry.definition.key}' returned invalid files.`,
				),
			);
		}
	}

	async function handleAiMessage(requestId: string, message: Record<string, unknown>) {
		function fail(code: string, detail: string) {
			send({ type: AI_MESSAGE_ERROR_MESSAGE_TYPE, requestId, error: { code, message: detail } });
		}
		let aiMessage: CustomAppAiMessage;
		try {
			aiMessage = parseAiMessage(message);
		} catch (error) {
			return fail("INVALID_REQUEST", error instanceof Error ? error.message : "The message is invalid.");
		}
		// The host checks the click itself, so an app cannot start runs without the user acting in it.
		if (!isUserActivationActive()) {
			return fail(
				"USER_ACTIVATION_REQUIRED",
				"Send messages to Aavo AI only from a user action, such as a click.",
			);
		}
		if (messageHandler == null) return fail("AI_UNAVAILABLE", "Aavo AI is not available.");
		try {
			await messageHandler(aiMessage);
			send({ type: AI_MESSAGE_RESULT_MESSAGE_TYPE, requestId });
		} catch (error) {
			if (error instanceof CustomAppAiMessageError) return fail(error.code, error.message);
			fail(
				"AI_MESSAGE_FAILED",
				error instanceof Error && error.message !== "" ? error.message : "Aavo AI could not start.",
			);
		}
	}

	return {
		catalog: () => catalog,
		subscribe(listener: () => void) {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		},
		isCallInProgress: () => pending.size > 0,
		callTool(
			toolKey: string,
			input: unknown,
			files: ReadonlyMap<string, File> = new Map(),
		): Promise<CustomAppAiToolResult> {
			if (disposed)
				return Promise.reject(new CustomAppAiToolCallError("APP_CLOSED", "The Custom App is closed."));
			const entry = entries.get(toolKey);
			if (!entry) {
				return Promise.reject(
					new CustomAppAiToolCallError(
						"AI_TOOL_NOT_AVAILABLE",
						`AI tool '${toolKey}' is not available in the open app.`,
					),
				);
			}
			if (!entry.validateInput(input)) {
				return Promise.reject(
					new CustomAppAiToolCallError(
						"INVALID_INPUT",
						`Input for AI tool '${toolKey}' does not match its input schema: ` +
							ajv.errorsText(entry.validateInput.errors),
					),
				);
			}
			const requestId = crypto.randomUUID();
			return new Promise<CustomAppAiToolResult>((resolve, reject) => {
				const timer = setTimeout(() => {
					pending.delete(requestId);
					reject(
						new CustomAppAiToolCallError(
							"TIMEOUT",
							`AI tool '${toolKey}' did not respond within ${Math.round(timeoutMs / 1000)} seconds.`,
						),
					);
				}, timeoutMs);
				pending.set(requestId, { entry, resolve, reject, timer });
				try {
					send({ type: AI_TOOL_CALL_MESSAGE_TYPE, requestId, toolKey, input: replaceTokens(input, files) });
				} catch {
					pending.delete(requestId);
					clearTimeout(timer);
					reject(new CustomAppAiToolCallError("APP_CLOSED", "The Custom App is not connected."));
				}
			});
		},
		setMessageHandler(handler: CustomAppAiMessageHandler | null) {
			messageHandler = handler;
		},
		handleMessage(message: unknown): boolean {
			if (!isRecord(message)) return false;
			if (message.type === AI_CATALOG_MESSAGE_TYPE) {
				if (!disposed) handleCatalogMessage(message);
				return true;
			}
			if (message.type === AI_MESSAGE_MESSAGE_TYPE) {
				if (!disposed && typeof message.requestId === "string")
					void handleAiMessage(message.requestId, message);
				return true;
			}
			if (message.type !== AI_TOOL_RESULT_MESSAGE_TYPE && message.type !== AI_TOOL_ERROR_MESSAGE_TYPE)
				return false;
			if (typeof message.requestId === "string") finishCall(message.requestId, message);
			return true;
		},
		dispose() {
			disposed = true;
			for (const call of pending.values()) {
				clearTimeout(call.timer);
				call.reject(new CustomAppAiToolCallError("APP_CLOSED", "The Custom App was closed."));
			}
			pending.clear();
			messageHandler = null;
			replaceCatalog(EMPTY_CATALOG, new Map());
			listeners.clear();
		},
	};
}

function parseAiMessage(message: Record<string, unknown>): CustomAppAiMessage {
	if (
		typeof message.message !== "string" ||
		message.message.trim() === "" ||
		message.message.length > MAX_AI_MESSAGE_LENGTH
	) {
		throw new Error(`The message must be a non-empty string of at most ${MAX_AI_MESSAGE_LENGTH} characters.`);
	}
	if (
		typeof message.label !== "string" ||
		message.label.trim() === "" ||
		message.label.length > MAX_AI_MESSAGE_LABEL_LENGTH
	) {
		throw new Error(`The label must be a non-empty string of at most ${MAX_AI_MESSAGE_LABEL_LENGTH} characters.`);
	}
	const skill = message.skill;
	if (
		skill !== undefined &&
		(typeof skill !== "string" || skill.length > MAX_AI_SKILL_NAME_LENGTH || !AI_SKILL_NAME.test(skill))
	) {
		throw new Error(
			`The skill must be an AI skill name of lowercase letters, digits and single hyphens, ` +
				`at most ${MAX_AI_SKILL_NAME_LENGTH} characters.`,
		);
	}
	return {
		message: message.message.trim(),
		label: message.label.trim(),
		...(skill === undefined ? {} : { skill }),
	};
}

function createAjv() {
	const ajv = new Ajv2020({ allErrors: true, strict: false });
	ajv.addFormat(AI_FILE_FORMAT, AI_FILE_TOKEN_PATTERN);
	return ajv;
}

/** Replaces each token string with its file. */
function replaceTokens(value: unknown, files: ReadonlyMap<string, File>): unknown {
	if (files.size === 0) return value;
	if (typeof value === "string") return files.get(value) ?? value;
	if (Array.isArray(value)) return value.map((item) => replaceTokens(item, files));
	if (isRecord(value)) {
		return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, replaceTokens(item, files)]));
	}
	return value;
}

/** Checks the files the app returned with its output: each one appears in the output as its token. */
function parseOutputFiles(toolKey: string, output: unknown, value: unknown): Map<string, File> {
	const files = new Map<string, File>();
	for (const entry of Array.isArray(value) ? value : []) {
		if (!isRecord(entry) || typeof entry.token !== "string" || !(entry.file instanceof File)) {
			throw new Error(`AI tool '${toolKey}' returned an invalid file.`);
		}
		files.set(entry.token, entry.file);
	}
	if (files.size > MAX_AI_TOOL_FILES) {
		throw new Error(`AI tool '${toolKey}' returned more than ${MAX_AI_TOOL_FILES} files.`);
	}
	let totalBytes = 0;
	for (const file of files.values()) {
		if (file.size > MAX_AI_TOOL_FILE_BYTES) {
			throw new Error(
				`AI tool '${toolKey}' returned file '${file.name}', which exceeds ${megabytes(MAX_AI_TOOL_FILE_BYTES)} MiB.`,
			);
		}
		totalBytes += file.size;
	}
	if (totalBytes > MAX_AI_TOOL_TOTAL_FILE_BYTES) {
		throw new Error(
			`AI tool '${toolKey}' returned files that exceed ${megabytes(MAX_AI_TOOL_TOTAL_FILE_BYTES)} MiB together.`,
		);
	}
	const tokens = new Set(fileTokens(output));
	if (tokens.size !== files.size || [...files.keys()].some((token) => !tokens.has(token))) {
		throw new Error(`AI tool '${toolKey}' returned files that do not match its output.`);
	}
	return files;
}

function fileTokens(value: unknown): string[] {
	if (typeof value === "string") return AI_FILE_TOKEN_PATTERN.test(value) ? [value] : [];
	if (Array.isArray(value)) return value.flatMap(fileTokens);
	if (isRecord(value)) return Object.values(value).flatMap(fileTokens);
	return [];
}

function megabytes(bytes: number) {
	return bytes / (1024 * 1024);
}

function parseCatalog(message: Record<string, unknown>): CustomAppAiCatalog {
	const guidance = message.guidance ?? null;
	if (guidance !== null && (typeof guidance !== "string" || guidance.length > MAX_AI_GUIDANCE_LENGTH)) {
		throw new Error(`AI guidance must be a string of at most ${MAX_AI_GUIDANCE_LENGTH} characters.`);
	}
	if (!Array.isArray(message.tools) || message.tools.length > MAX_AI_TOOLS) {
		throw new Error(`An app can register at most ${MAX_AI_TOOLS} AI tools.`);
	}
	const keys = new Set<string>();
	const tools = message.tools.map((value: unknown): CustomAppAiTool => {
		if (!isRecord(value) || typeof value.key !== "string" || !AI_TOOL_KEY_PATTERN.test(value.key)) {
			throw new Error("Each AI tool needs a key that starts with a letter and has at most 64 characters.");
		}
		const key = value.key;
		if (keys.has(key)) throw new Error(`AI tool '${key}' is registered more than once.`);
		keys.add(key);
		if (
			typeof value.description !== "string" ||
			value.description.trim() === "" ||
			value.description.length > MAX_AI_TOOL_DESCRIPTION_LENGTH
		) {
			throw new Error(
				`AI tool '${key}' needs a description of at most ${MAX_AI_TOOL_DESCRIPTION_LENGTH} characters.`,
			);
		}
		if (!isRecord(value.inputSchema) || value.inputSchema.type !== "object") {
			throw new Error(`AI tool '${key}' needs an object input schema.`);
		}
		if (value.outputSchema !== undefined && !isRecord(value.outputSchema)) {
			throw new Error(`AI tool '${key}' has an invalid output schema.`);
		}
		return {
			key,
			description: value.description,
			inputSchema: value.inputSchema,
			...(value.outputSchema === undefined ? {} : { outputSchema: value.outputSchema }),
		};
	});
	return { guidance, tools, conversation: parseConversation(message.conversation) };
}

function parseConversation(value: unknown): CustomAppAiConversation | null {
	if (value == null) return null;
	if (
		!isRecord(value) ||
		typeof value.key !== "string" ||
		value.key.trim() === "" ||
		value.key.length > MAX_AI_CONVERSATION_KEY_LENGTH
	) {
		throw new Error(
			`AI conversation key must be a non-empty string of at most ${MAX_AI_CONVERSATION_KEY_LENGTH} characters.`,
		);
	}
	const label = value.label ?? null;
	if (label !== null && (typeof label !== "string" || label.length > MAX_AI_CONVERSATION_LABEL_LENGTH)) {
		throw new Error(
			`AI conversation label must be a string of at most ${MAX_AI_CONVERSATION_LABEL_LENGTH} characters.`,
		);
	}
	return { key: value.key, label: label?.trim() || null };
}

function compileSchema(
	ajv: Ajv2020,
	key: string,
	kind: "input" | "output",
	schema: Record<string, unknown>,
): ValidateFunction {
	try {
		return ajv.compile(schema);
	} catch (error) {
		const reason = error instanceof Error ? error.message : "invalid schema";
		throw new Error(`AI tool '${key}' has an invalid ${kind} schema: ${reason}`, { cause: error });
	}
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
